import React, { useState, useEffect } from "react";
import { Users, Shield, Plus, X, Edit2, Trash2, Key, UserPlus, Code, Zap, Target, Activity, Monitor, Wrench, Briefcase, Heart, Star, Compass, Anchor, Sailboat } from "lucide-react";
import { Spinner, UserAvatar } from "./App"; 

const AVAILABLE_ICONS = [
  { name: "Users", icon: Users }, { name: "Shield", icon: Shield }, { name: "Code", icon: Code },
  { name: "Zap", icon: Zap }, { name: "Target", icon: Target }, { name: "Activity", icon: Activity },
  { name: "Monitor", icon: Monitor }, { name: "Wrench", icon: Wrench }, { name: "Briefcase", icon: Briefcase },
  { name: "Heart", icon: Heart }, { name: "Star", icon: Star }, { name: "Compass", icon: Compass },
  { name: "Anchor", icon: Anchor }, { name: "Sailboat", icon: Sailboat },
];

export default function TeamsTab({ ctx }) {
  const { api, T, currentUser, isAdmin, isEN, notify } = ctx;

  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ name: "", icon: "Users", color: "#0ECFD9", leader_id: "" });

  const [manageTeam, setManageTeam] = useState(null);
  const [memberSelect, setMemberSelect] = useState("");
  
  // 🚀 NEU: Team-Spezifischer Skill Editor States
  const [skillManageData, setSkillManageData] = useState(null); // { teamId, userId, name, skills, color }
  const [newSkill, setNewSkill] = useState("");

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api("/workspace/teams");
      setTeams(res.teams || []);
      setAllUsers(res.allUsers || []);
      
      // 🚀 FIX: Greift immer auf den absolut neuesten State des Modals zu
      setManageTeam(prev => {
        if (!prev) return null;
        const updatedTeam = (res.teams || []).find(t => t.id === prev.id);
        return updatedTeam || prev;
      });
      
    } catch (e) {
      notify("Fehler beim Laden: " + e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const handleUpdate = () => loadData(true);
    window.addEventListener("teams_updated", handleUpdate);
    window.addEventListener("users_updated", handleUpdate);
    return () => {
      window.removeEventListener("teams_updated", handleUpdate);
      window.removeEventListener("users_updated", handleUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveTeam = async () => {
    if (!form.name.trim()) return notify(isEN ? "Name required" : "Name erforderlich");
    try {
      if (editTarget) {
        await api(`/workspace/teams/${editTarget.id}`, { method: "PUT", body: JSON.stringify(form) });
        notify(isEN ? "Team updated" : "Team aktualisiert");
      } else {
        await api("/workspace/teams", { method: "POST", body: JSON.stringify(form) });
        notify(isEN ? "Team created" : "Team erstellt");
      }
      setShowModal(false);
      loadData(true);
    } catch (e) { notify("Fehler: " + e.message); }
  };

  const handleDeleteTeam = async (id) => {
    if (!window.confirm(isEN ? "Delete this team?" : "Dieses Team wirklich löschen?")) return;
    try {
      await api(`/workspace/teams/${id}`, { method: "DELETE" });
      notify(isEN ? "Team deleted" : "Team gelöscht");
      loadData(true);
    } catch (e) { notify("Fehler: " + e.message); }
  };

  const handleAddMember = async () => {
    if (!memberSelect) return;
    const uid = parseInt(memberSelect);
    const userToAdd = allUsers.find(u => u.id === uid);

    // 🚀 OPTIMISTIC UPDATE: Sofort visuell eintragen!
    if (userToAdd) {
      setManageTeam(prev => ({
        ...prev,
        members: [...(prev.members || []), { user_id: userToAdd.id, name: userToAdd.name, avatar_url: userToAdd.avatar_url, team_skills: [] }]
      }));
    }

    try {
      await api(`/workspace/teams/${manageTeam.id}/members`, {
        method: "POST",
        body: JSON.stringify({ user_id: uid }),
      });
      setMemberSelect("");
      loadData(true);
    } catch (e) { 
      notify("Fehler: " + e.message); 
      loadData(true); // Rollback bei Fehler
    }
  };

  const handleRemoveMember = async (userId) => {
    // 🚀 OPTIMISTIC UPDATE: Sofort visuell entfernen!
    setManageTeam(prev => ({
      ...prev,
      members: (prev.members || []).filter(m => m.user_id !== userId)
    }));

    try {
      await api(`/workspace/teams/${manageTeam.id}/members/${userId}`, { method: "DELETE" });
      loadData(true);
    } catch (e) { 
      notify("Fehler: " + e.message); 
      loadData(true); // Rollback bei Fehler
    }
  };

  const resetUserPassword = async (teamId, userId, userName) => {
    if (!window.confirm(`Passwort für ${userName} wirklich zurücksetzen?`)) return;
    try {
      const res = await api(`/workspace/teams/${teamId}/reset-password/${userId}`, { method: "POST" });
      window.prompt(`Neues Einmal-Passwort für ${userName}: (Bitte kopieren und sicher übergeben)`, res.otp);
    } catch (e) { notify("Fehler beim Reset: " + e.message); }
  };

  // 🚀 NEU: Skill im spezifischen Team speichern
  const handleAddTeamSkill = async () => {
    if (!newSkill.trim()) return;
    const updatedSkills = [...(skillManageData.skills || []), newSkill.trim()];
    try {
      await api(`/workspace/teams/${skillManageData.teamId}/members/${skillManageData.userId}/skills`, { 
        method: "PUT", body: JSON.stringify({ skills: updatedSkills }) 
      });
      setSkillManageData({...skillManageData, skills: updatedSkills});
      setNewSkill("");
      loadData(true);
    } catch(e) { notify("Fehler: " + e.message); }
  };

  // 🚀 NEU: Skill aus dem Team entfernen
  const handleRemoveTeamSkill = async (skillToRemove) => {
    const updatedSkills = (skillManageData.skills || []).filter(s => s !== skillToRemove);
    try {
      await api(`/workspace/teams/${skillManageData.teamId}/members/${skillManageData.userId}/skills`, { 
        method: "PUT", body: JSON.stringify({ skills: updatedSkills }) 
      });
      setSkillManageData({...skillManageData, skills: updatedSkills});
      loadData(true);
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const openCreate = () => { setEditTarget(null); setForm({ name: "", icon: "Users", color: "#0ECFD9", leader_id: "" }); setShowModal(true); };
  const openEdit = (t) => { setEditTarget(t); setForm({ name: t.name, icon: t.icon || "Users", color: t.color || "#0ECFD9", leader_id: t.leader_id || "" }); setShowModal(true); };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: T.textMuted }}><Spinner /></div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)", animation: "fadeUp 0.3s ease", maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <Shield size={24} color="#8B5CF6" /> Workspace Management
          </h2>
          <div style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
            Verwalte Teams, Zuweisungen und Team-Skills.
          </div>
        </div>
        {isAdmin && (
          <button onClick={openCreate} className="hov" style={{ display: "flex", alignItems: "center", gap: 8, background: "#8B5CF6", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 12, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(139,92,246,0.3)" }}>
            <Plus size={18} /> {isEN ? "New Team" : "Neues Team"}
          </button>
        )}
      </div>

      <div className="custom-scrollbar" style={{ flex: 1, overflowY: "auto", paddingBottom: 40, paddingRight: 8 }}>
        
        {/* TEAM GRID */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 20 }}>
          {teams.map((t) => {
            const amILeader = t.leader_id === currentUser.id;
            const canManage = isAdmin || amILeader;
            const TeamIconComponent = AVAILABLE_ICONS.find(i => i.name === t.icon)?.icon || Users;

            return (
              <div key={t.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                <div style={{ padding: "20px", borderBottom: `2px solid ${t.color}`, background: T.cardHover, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${t.color}20`, display: "flex", alignItems: "center", justifyContent: "center", color: t.color }}>
                      <TeamIconComponent size={24} />
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                        <Users size={12} /> {(t.members || []).length} {isEN ? "Members" : "Mitglieder"}
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => openEdit(t)} className="hov" style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><Edit2 size={16} /></button>
                      <button onClick={() => handleDeleteTeam(t.id)} className="hov" style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer" }}><Trash2 size={16} /></button>
                    </div>
                  )}
                </div>

                <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.1em", marginBottom: 8 }}>TEAMLEITUNG</div>
                    {t.leader_id ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, background: T.bg, padding: "8px 12px", borderRadius: 10, border: `1px solid ${T.border2}` }}>
                        <UserAvatar user={{ id: t.leader_id, name: t.leader_name, avatar_url: t.leader_avatar }} size={32} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{t.leader_name}</div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: T.textMuted, fontStyle: "italic" }}>{isEN ? "No leader assigned" : "Keine Leitung zugewiesen"}</div>
                    )}
                  </div>

                  {canManage && (
                    <button onClick={() => setManageTeam(t)} className="hov" style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: `${t.color}15`, color: t.color, border: `1px solid ${t.color}40`, padding: "10px", borderRadius: 10, fontWeight: 700, cursor: "pointer", width: "100%" }}>
                      <UserPlus size={16} /> {isEN ? "Manage Members" : "Mitglieder verwalten"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card, borderRadius: 20, width: "100%", maxWidth: 500, border: `1px solid ${T.border}`, boxShadow: "0 20px 40px rgba(0,0,0,0.2)", overflow: "hidden", animation: "popIn 0.3s ease" }}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: T.cardHover }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{editTarget ? (isEN ? "Edit Team" : "Team bearbeiten") : (isEN ? "New Team" : "Neues Team")}</div>
              <button onClick={() => setShowModal(false)} className="hov" style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><X size={20}/></button>
            </div>
            
            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.textMuted, marginBottom: 8 }}>TEAM NAME</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. Scripting" style={{ width: "100%", background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, padding: "12px 16px", borderRadius: 12, fontSize: 15, outline: "none" }} />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.textMuted, marginBottom: 8 }}>TEAM ICON</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, background: T.bg, padding: 12, borderRadius: 12, border: `1px solid ${T.border2}` }}>
                  {AVAILABLE_ICONS.map((IconItem) => {
                    const isSelected = form.icon === IconItem.name;
                    return (
                      <button key={IconItem.name} onClick={() => setForm({ ...form, icon: IconItem.name })} title={IconItem.name} className="hov" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 10, cursor: "pointer", background: isSelected ? `${form.color}20` : "transparent", border: isSelected ? `2px solid ${form.color}` : `1px solid ${T.border}`, color: isSelected ? form.color : T.textMuted, transition: "all 0.2s ease" }}>
                        <IconItem.icon size={20} />
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.textMuted, marginBottom: 8 }}>TEAM FARBE</label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input type="color" value={form.color || '#0ECFD9'} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 45, height: 45, padding: 0, border: "none", borderRadius: 12, cursor: "pointer", background: "transparent" }} />
                  <input type="text" value={form.color || '#0ECFD9'} onChange={e => setForm({ ...form, color: e.target.value })} style={{ flex: 1, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, padding: "12px", borderRadius: 12, fontSize: 15, outline: "none", textTransform: "uppercase" }} />
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.textMuted, marginBottom: 8 }}>TEAMLEITUNG (Optional)</label>
                <select value={form.leader_id} onChange={e => setForm({...form, leader_id: e.target.value})} style={{ width: "100%", background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, padding: "12px 16px", borderRadius: 12, fontSize: 15, outline: "none", cursor: "pointer" }}>
                  <option value="">-- Keine Leitung --</option>
                  {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>

              <button onClick={handleSaveTeam} className="hov" style={{ background: "#8B5CF6", color: "#fff", border: "none", padding: 16, borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: "pointer", marginTop: 10 }}>
                {isEN ? "Save Team" : "Team speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MANAGE MEMBERS MODAL */}
      {manageTeam && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card, borderRadius: 20, width: "100%", maxWidth: 650, border: `1px solid ${T.border}`, boxShadow: "0 20px 40px rgba(0,0,0,0.2)", overflow: "hidden", animation: "popIn 0.3s ease", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
            
            <div style={{ padding: "20px 24px", borderBottom: `2px solid ${manageTeam.color}`, background: T.cardHover, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${manageTeam.color}20`, display: "flex", alignItems: "center", justifyContent: "center", color: manageTeam.color }}>
                  {(() => {
                    const ManageTeamIcon = AVAILABLE_ICONS.find(i => i.name === manageTeam.icon)?.icon || Users;
                    return <ManageTeamIcon size={20} />;
                  })()}
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{manageTeam.name}</div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{isEN ? "Member Management" : "Mitgliederverwaltung"}</div>
                </div>
              </div>
              <button onClick={() => setManageTeam(null)} className="hov" style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><X size={20}/></button>
            </div>
            
            <div style={{ padding: 24, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.textMuted, marginBottom: 8 }}>MITGLIED HINZUFÜGEN</label>
              <div style={{ display: "flex", gap: 12 }}>
                <select value={memberSelect} onChange={e => setMemberSelect(e.target.value)} style={{ flex: 1, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, padding: "12px 16px", borderRadius: 12, fontSize: 15, outline: "none", cursor: "pointer" }}>
                  <option value="">-- Nutzer auswählen --</option>
                  {allUsers.filter(u => !(manageTeam.members || []).find(m => m.user_id === u.id)).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <button onClick={handleAddMember} disabled={!memberSelect} className="hov" style={{ background: manageTeam.color, color: "#000", border: "none", padding: "0 24px", borderRadius: 12, fontWeight: 800, cursor: memberSelect ? "pointer" : "not-allowed", opacity: memberSelect ? 1 : 0.5 }}>
                  {isEN ? "Add" : "Hinzufügen"}
                </button>
              </div>
            </div>

            <div className="custom-scrollbar" style={{ flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.1em", marginBottom: 4 }}>AKTUELLE MITGLIEDER ({(manageTeam.members || []).length})</div>
              
              {(manageTeam.members || []).length === 0 && (
                <div style={{ textAlign: "center", padding: 20, color: T.textGhost, fontStyle: "italic" }}>{isEN ? "No members yet." : "Noch keine Mitglieder in diesem Team."}</div>
              )}

              {/* 🚀 NEU: Mitglieder-Liste inkl. Team-Skills */}
              {(manageTeam.members || []).map(m => (
                <div key={m.user_id} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", background: T.bg, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border2}` }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <UserAvatar user={m} size={36} />
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{m.name}</div>
                        {manageTeam.leader_id === m.user_id && <div style={{ fontSize: 10, color: "#F59E0B", fontWeight: 700, background: "#F59E0B20", padding: "2px 6px", borderRadius: 6 }}>TEAMLEITUNG</div>}
                      </div>
                      
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {(m.team_skills || []).map(skill => (
                          <span key={skill} style={{ fontSize: 10, background: `${manageTeam.color}20`, color: manageTeam.color, padding: "2px 8px", borderRadius: 6, fontWeight: 700 }}>
                            {skill}
                          </span>
                        ))}
                      </div>
                      
                      <button onClick={() => setSkillManageData({ teamId: manageTeam.id, userId: m.user_id, name: m.name, skills: m.team_skills || [], color: manageTeam.color })} className="hov" style={{ background: "transparent", border: "none", color: T.textFaint, padding: "4px 0", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                        <Edit2 size={10} /> Skills verwalten
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => resetUserPassword(manageTeam.id, m.user_id, m.name)} title={isEN ? "Reset Password" : "Passwort zurücksetzen"} className="hov" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, background: "rgba(14, 207, 217, 0.1)", border: "1px solid rgba(14, 207, 217, 0.2)", color: "#0ECFD9", borderRadius: 10, cursor: "pointer" }}>
                      <Key size={16} />
                    </button>
                    <button onClick={() => handleRemoveMember(m.user_id)} title={isEN ? "Remove from team" : "Aus Team entfernen"} className="hov" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, background: "rgba(255, 71, 87, 0.1)", border: "1px solid rgba(255, 71, 87, 0.2)", color: "#FF4757", borderRadius: 10, cursor: "pointer" }}>
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 🚀 NEU: SKILL EDITOR FÜR EINZELNES TEAM-MITGLIED */}
      {skillManageData && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.sidebar, borderRadius: 20, width: "100%", maxWidth: 450, border: `1px solid ${T.border3}`, boxShadow: "0 20px 40px rgba(0,0,0,0.4)", overflow: "hidden", animation: "popIn 0.2s ease" }}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: T.cardHover }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Skills von {skillManageData.name}</div>
                  <div style={{ fontSize: 12, color: skillManageData.color }}>Nur für dieses Team gültig</div>
                </div>
              </div>
              <button onClick={() => setSkillManageData(null)} className="hov" style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><X size={20}/></button>
            </div>
            
            <div style={{ padding: 24 }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <input 
                  value={newSkill} 
                  onChange={e => setNewSkill(e.target.value)} 
                  onKeyDown={e => e.key === "Enter" && handleAddTeamSkill()}
                  placeholder="Neuen Skill eingeben..." 
                  style={{ flex: 1, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, padding: "10px 14px", borderRadius: 10, fontSize: 14, outline: "none" }} 
                  autoFocus
                />
                <button onClick={handleAddTeamSkill} disabled={!newSkill.trim()} className="hov" style={{ background: skillManageData.color, color: "#000", border: "none", padding: "0 16px", borderRadius: 10, fontWeight: 700, cursor: "pointer", opacity: newSkill.trim() ? 1 : 0.5 }}>
                  <Plus size={18} />
                </button>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.1em", marginBottom: 12 }}>AKTUELLE TEAM-SKILLS</div>
              
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(skillManageData.skills || []).length === 0 && <div style={{ fontSize: 13, color: T.textFaint, fontStyle: "italic" }}>Noch keine Skills in diesem Team zugewiesen.</div>}
                {(skillManageData.skills || []).map(skill => (
                  <div key={skill} style={{ display: "flex", alignItems: "center", gap: 8, background: `${skillManageData.color}15`, border: `1px solid ${skillManageData.color}30`, color: skillManageData.color, padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                    {skill}
                    <button onClick={() => handleRemoveTeamSkill(skill)} style={{ background: "transparent", border: "none", color: skillManageData.color, cursor: "pointer", display: "flex", alignItems: "center" }} className="hov">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}